import { concat, get, isEqual, isUndefined, merge, omit } from "lodash";
import { buildTree, Tree } from "utils/tree";
import { applyFunction } from "./action";
import { buildPath } from "./common";
import { buildDependencyIndex } from "./dependencyIndex";
import {
  IBase,
  ICalculate,
  IExplorer,
  IGetPath,
  IGetValue,
  IInput,
  ILayout,
  IList,
  IMetadata,
  IModel,
  IName,
  IOutput,
  ISideEffects,
  ITab,
  ITabs,
  ITemplate,
  IValue,
  SideEffectType,
} from "./types";

/**
 * Seeds the initial list of hidden nodes.
 * @param items
 * @returns initial hidden nodes
 */
const seedHidden = (items: Array<IBase>): Array<IName> => {
  const hide = (n: IName, e: IExplorer) => {
    if (e.includes) {
      return e.includes.includes(n);
    } else if (e.excludes) {
      return !e.excludes.includes(n);
    } else {
      return true;
    }
  };
  const hidden = [] as Array<IName>;
  const build = (v: IBase, e?: IExplorer): void => {
    switch (v.type) {
      // the driver of hiding children
      case "explorer":
        (v as IExplorer).items.forEach((i) => build(i, v as IExplorer));
        break;
      // these types can be nested
      case "tab":
        (v as ITab).items.forEach((i) => build(i));
        break;
      // these types can be nested and hidden
      case "tabs":
        if (e && hide(v.name, e)) {
          hidden.push(v.name);
        }
        (v as ITabs).items.forEach((i) => build(i, e));
        break;
      case "list":
      case "group":
      case "anchors":
        if (e && hide(v.name, e)) {
          hidden.push(v.name);
        }
        (v as ILayout<IBase>).items.forEach((i) => build(i, e));
        break;
      // these types are not nested and can be hidden
      case "3d_surface":
      case "bar_chart":
      case "waterfall_chart":
      case "pie_chart":
      case "time_chart":
        if (e && hide(v.name, e)) {
          hidden.push(v.name);
        }
        break;
      // these types can't be hidden individually
      case "number":
      case "text":
      case "select":
      case "checkbox":
      case "file":
      case "radio":
      case "range":
      case "label":
      case "image":
      default:
        break;
    }
  };
  items.forEach((i) => build(i));
  return hidden;
};

/**
 * Seeds the initial list of overridden nodes.
 * @param items
 * @returns initial overridden nodes
 */
const seedOverridden = (items: Array<IBase>, model: IModel): Array<IName> => {
  const getValue = ((p) => get(model, p)) as IGetValue;
  const getPath = ((p) => p) as IGetPath;
  const overridden = [] as Array<IName>;
  const build = (v: IBase, l?: { name: IName; index: number }): void => {
    const gvw = l ? (((p: IName) => getValue(buildPath(l.name, l.index, p))) as IGetValue) : getValue;
    const gpw = l ? (((p: IName) => buildPath(l.name, l.index, p)) as IGetPath) : getPath;
    switch (v.type) {
      // these types can be nested
      case "tabs":
        (v as ITabs).items.forEach((i) => build(i, l));
        break;
      case "explorer":
      case "tab":
      case "group":
      case "anchors":
        (v as ILayout<IBase>).items.forEach((i) => build(i, l));
        break;
      case "list":
        (gvw((v as IList).name) as Array<any>).forEach((_a, n) =>
          (v as IList).items.map((i) => build(i, { name: (v as IList).name, index: n }))
        );
        break;
      // these types are not nested and can't be overridden
      case "3d_surface":
      case "bar_chart":
      case "waterfall_chart":
      case "pie_chart":
      case "time_chart":
      case "label":
      case "image":
      case "calculate":
        break;
      // these types can be overridden
      case "number":
      case "text":
      case "select":
      case "checkbox":
      case "radio":
      case "range":
      case "file":
      case "location":
      case "location_matrix":
        const t = v as IInput;
        const d = t.default ? (applyFunction(model, t.default, gvw) as IValue) : undefined;
        if (d !== undefined && !isEqual(gvw(t.name), d)) {
          overridden.push(gpw(t.name));
        }
        break;
      default:
        break;
    }
  };
  items.forEach((i) => build(i));
  return overridden;
};

/**
 * Builds the list of overridden fields.
 * @param template
 * @param model
 */
export const buildOverridden = (template: ITemplate, model: IModel): Array<IName> => {
  return seedOverridden(template.inputs, model);
};

const hasSideEffect = (item: IInput | IOutput | ILayout<IBase>) => {
  switch (item.type) {
    case "number":
    case "text":
    case "select":
    case "checkbox":
    case "file":
    case "list":
    case "radio":
    case "range":
    case "location":
    case "location_matrix":
      return !isUndefined(item.default);
    case "calculate":
      return !isUndefined(item.default) || !isUndefined((item as ICalculate).query);
  }
};

/**
 * Seeds the initial list of input or output items that have associated side effects.
 * @param template
 * @param model
 * @returns initial items
 */
function seedSideEffects(template: ITemplate, model: IModel) {
  const getValue = ((p, m, r) => get(m || model, p) || get(r, p)) as IGetValue;
  const getPath = ((p) => p) as IGetPath;
  const effects = [] as ISideEffects;
  const build = (v: IBase, type: SideEffectType, l?: { name: IName; index: number }): void => {
    const gvw = l ? (((p, m) => getValue(buildPath(l.name, l.index, p), m)) as IGetValue) : getValue;
    const gpw = l ? (((p) => buildPath(l.name, l.index, p)) as IGetPath) : getPath;
    switch (v.type) {
      // these types can be nested
      case "explorer":
      case "tab":
      case "group":
      case "tabs":
      case "anchors":
        (v as ILayout<IBase>).items.forEach((i) => build(i, type, l));
        break;
      case "list":
        if (hasSideEffect(v as IInput)) {
          effects.push({ template: v as IInput, getValue: gvw, path: gpw(v.name), type });
        }
        (gvw((v as IList).name) as Array<any>).forEach((_a, n) =>
          (v as IList).items.map((i) => build(i, type, { name: (v as IList).name, index: n }))
        );
        break;
      // these types are not nested and can't have side effects
      case "label":
      case "image":
        break;
      case "3d_surface":
      case "bar_chart":
      case "waterfall_chart":
      case "pie_chart":
      case "time_chart":
        break;
      // these types are not nested and can have side effects
      case "number":
      case "text":
      case "select":
      case "checkbox":
      case "file":
      case "radio":
      case "range":
      case "location":
      case "location_matrix":
      case "calculate":
        if (hasSideEffect(v as IInput)) {
          effects.push({ template: v as IInput, getValue: gvw, path: gpw(v.name), type });
        }
        break;
      default:
        break;
    }
  };
  template.inputs.forEach((i) => build(i, SideEffectType.Input));
  template.outputs.forEach((i) => build(i, SideEffectType.Output));
  return effects;
}

/**
 * Builds the list of input side effect components.
 * @param template
 * @param model
 * @returns
 */
export const buildSideEffects = (template: ITemplate, model: IModel) => {
  return seedSideEffects(template, model);
};

const enum IItemType {
  Inputs = "inputs",
  Outputs = "outputs",
}

/**
 * Seed input or output items for figure lookup.
 * @param type
 * @param template
 * @param model
 * @returns
 */
function seedItems(type: IItemType, template: ITemplate, model: IModel): Tree<Partial<IBase>> {
  const getValue = ((p) => get(model, p)) as IGetValue;
  const temp = [] as Array<Partial<IBase>>;
  const items = type === IItemType.Inputs ? template.inputs : template.outputs;
  const build = (v: IBase, l?: { name: IName; index: number }, parent?: IName): void => {
    const gvw = l ? (((p: IName) => getValue(buildPath(l.name, l.index, p))) as IGetValue) : getValue;
    switch (v.type) {
      // these types can be nested
      case "explorer":
      case "tab":
      case "group":
      case "tabs":
      case "anchors":
        temp.push(merge(omit(v as ILayout<IBase>, ["items"]), { parent }));
        (v as ILayout<IBase>).items.forEach((i) => build(i, l, v.name));
        break;
      case "list":
        temp.push(merge(omit(v as IList, ["items"]), { parent }));
        (gvw((v as IList).name) as Array<any>).forEach((_a, n) =>
          (v as IList).items.map((i) => build(i, { name: (v as IList).name, index: n }, v.name))
        );
        break;
      // these types are not nested
      case "label":
      case "image":
        temp.push(merge(omit(v as ILayout<IBase>, ["items"]), { parent }));
        break;
      case "3d_surface":
      case "bar_chart":
      case "waterfall_chart":
      case "pie_chart":
      case "time_chart":
        temp.push(merge(omit(v as IOutput, ["items"]), { parent }));
        break;
      case "number":
      case "text":
      case "select":
      case "checkbox":
      case "file":
      case "radio":
      case "range":
      case "location":
      case "location_matrix":
      case "calculate":
        temp.push(merge(omit(v as IInput, ["items"]), { parent }));
        break;
      default:
        break;
    }
  };
  items.forEach((i) => build(i));
  return buildTree(temp, { id: "name", parentId: "parent" });
}

/**
 * Builds a defaut metadata object.
 * @param template
 * @param model
 * @returns
 */
export const buildMetadata = (template?: ITemplate, model?: IModel): IMetadata => {
  const items: Array<IBase> | undefined = template ? concat(template?.inputs, template?.outputs) : undefined;
  return {
    hidden: items ? seedHidden(items) : [],
    loading: [],
    faults: {},
    overridden: items && model ? seedOverridden(items, model) : [],
    effects: template && model ? seedSideEffects(template, model) : [],
    inputs: template && model ? seedItems(IItemType.Inputs, template, model) : undefined,
    outputs: template && model ? seedItems(IItemType.Outputs, template, model) : undefined,
    properties: {},
    dependencyIndex: buildDependencyIndex(template, model),
    initializing: true,
    initialLoading: [],
    hiddenInput: [],
  };
};
